import React, { useState, useEffect } from "react";
import {
  Stack,
  Text,
  Button,
  Box,
  useMantineTheme,
  useMantineColorScheme,
  TextInput,
  Group,
  SimpleGrid,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { InfoIcon } from "@phosphor-icons/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUpdateProfile, useMe } from "@/api";
import type { Address } from "@/api/types";

interface ProfileCompletionModalProps {
  opened: boolean;
  // onClose?: () => void;
  onComplete: () => void;
  missingFields: {
    first_name: boolean;
    last_name: boolean;
    date_of_birth: boolean;
    address: boolean;
  };
}

const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  opened,
  // onClose,
  onComplete,
  missingFields,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [isMobile, setIsMobile] = useState(false);
  const { data: userData, refetch } = useMe();
  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateProfile();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getValidationSchema = () => {
    const schema: any = {};
    
    if (missingFields.first_name) {
      schema.first_name = Yup.string()
        .required("First name is required")
        .matches(/^[A-Za-z]+$/, "First name must contain only alphabetic characters (A-Z, a-z)")
        .min(1, "First name cannot be empty")
        .max(255, "First name cannot exceed 255 characters")
        .test("no-whitespace", "First name cannot contain whitespace", (value) => {
          if (!value) return true;
          return !/\s/.test(value);
        })
        .test("no-underscore", "First name cannot contain underscore", (value) => {
          if (!value) return true;
          return !value.includes("_");
        })
        .label("First Name");
    }
    
    if (missingFields.last_name) {
      schema.last_name = Yup.string()
        .required("Last name is required")
        .matches(/^[A-Za-z]+$/, "Last name must contain only alphabetic characters (A-Z, a-z)")
        .min(1, "Last name cannot be empty")
        .max(255, "Last name cannot exceed 255 characters")
        .test("no-whitespace", "Last name cannot contain whitespace", (value) => {
          if (!value) return true;
          return !/\s/.test(value);
        })
        .test("no-underscore", "Last name cannot contain underscore", (value) => {
          if (!value) return true;
          return !value.includes("_");
        })
        .label("Last Name");
    }
    
    if (missingFields.date_of_birth) {
      schema.date_of_birth = Yup.date()
        .max(new Date(), "Date of birth cannot be in the future")
        .min(new Date('1900-01-01'), "Date of birth cannot be before 1900")
        .nullable()
        .label("Date of Birth");
    }
    
    if (missingFields.address) {
      schema.address = Yup.object().shape({
        street_address: Yup.string()
          .required("Street address is required")
          .min(5, "Street address must be at least 5 characters"),
        city: Yup.string()
          .required("City is required")
          .min(2, "City must be at least 2 characters"),
        state: Yup.string()
          .required("State is required")
          .min(2, "State must be at least 2 characters"),
        zip_code: Yup.string()
          .required("Zip code is required")
          .test("length", "Zip code must be 4 to 5 digits", (value) => {
            if (!value) return false;
            return value.length >= 4 && value.length <= 5;
          })
          .matches(/^\d+$/, "Zip code must contain only numeric digits"),
        country: Yup.string()
          .required("Country is required")
          .min(2, "Country must be at least 2 characters"),
      });
    }
    
    return Yup.object().shape(schema);
  };

  const formik = useFormik({
    initialValues: {
      first_name: userData?.first_name || "",
      last_name: userData?.last_name || "",
      date_of_birth: userData?.date_of_birth 
        ? (typeof userData.date_of_birth === 'string' 
          ? new Date(userData.date_of_birth) 
          : userData.date_of_birth)
        : null,
      address: userData?.address ?? {
        street_address: "",
        city: "",
        state: "",
        country: "",
        zip_code: "",
      } as Address,
    },
    enableReinitialize: true,
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      try {
        const updateData: any = {};
        
        if (missingFields.first_name) {
          updateData.first_name = values.first_name;
        }
        if (missingFields.last_name) {
          updateData.last_name = values.last_name;
        }
        if (missingFields.date_of_birth) {
          updateData.date_of_birth = values.date_of_birth 
            ? (values.date_of_birth instanceof Date 
              ? values.date_of_birth.toISOString().split('T')[0] 
              : values.date_of_birth)
            : null;
        }
        if (missingFields.address) {
          updateData.address = values.address;
        }
        
        await updateUser({
          data: updateData,
        });
        
        await refetch();
        
        onComplete();
      } catch (error) {
        console.error("Failed to update profile:", error);
      }
    },
  });

  useEffect(() => {
    if (opened && userData) {
      formik.setValues({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        date_of_birth: userData.date_of_birth 
          ? (typeof userData.date_of_birth === 'string' 
            ? new Date(userData.date_of_birth) 
            : userData.date_of_birth)
          : null,
        address: userData.address ?? {
          street_address: "",
          city: "",
          state: "",
          country: "",
          zip_code: "",
        },
      });
    }
  }, [opened, userData]);

  const isFormValid = () => {
    if (missingFields.first_name) {
      if (!formik.values.first_name || formik.errors.first_name) {
        return false;
      }
    }
    
    if (missingFields.last_name) {
      if (!formik.values.last_name || formik.errors.last_name) {
        return false;
      }
    }
    
    if (missingFields.date_of_birth) {
      if (!formik.values.date_of_birth || formik.errors.date_of_birth) {
        return false;
      }
    }
    
    if (missingFields.address) {
      const address = formik.values.address;
      const addressErrors = formik.errors.address as any;
      
      if (!address.street_address || addressErrors?.street_address) {
        return false;
      }
      if (!address.city || addressErrors?.city) {
        return false;
      }
      if (!address.state || addressErrors?.state) {
        return false;
      }
      if (!address.zip_code || addressErrors?.zip_code) {
        return false;
      }
      if (!address.country || addressErrors?.country) {
        return false;
      }
    }
    
    return true;
  };

  if (!opened) return null;

  return (
    <>
      <Box
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          zIndex: 1000,
        }}
      />

      <Box
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1001,
        }}
      >
        <Box
          style={{
            backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "white",
            borderRadius: "8px",
            padding: isMobile ? "16px" : "32px",
            boxShadow: "0px 1px 4px 0px rgba(12, 12, 13, 0.05), 0px 1px 4px 0px rgba(12, 12, 13, 0.1)",
            minWidth: isMobile ? "320px" : "600px",
            maxWidth: "90vw",
            width: isMobile ? "90vw" : "600px",
            maxHeight: isMobile ? "85vh" : "90vh",
            overflowY: "auto",
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Stack gap={isMobile ? "16px" : "24px"}>
              <Text
                fw={510}
                size={isMobile ? "18px" : "20px"}
                c={colorScheme === "dark" ? "white" : "#1D1D1D"}
                ta="center"
                style={{ lineHeight: "1.2em" }}
              >
                Complete Your Profile
              </Text>

              <Box
                style={{
                  width: "100%",
                  maxWidth: isMobile ? "100%" : "741px",
                  backgroundColor: colorScheme === "dark" ? theme.colors.dark[5] : "#F5F5F5",
                  borderRadius: "8px",
                  padding: isMobile ? "12px" : "16px",
                }}
              >
                <Group gap="8px" align="flex-start">
                  <InfoIcon 
                    size={isMobile ? 18 : 20} 
                    color={colorScheme === "dark" ? theme.colors.gray[4] : "#393939"}
                    style={{ flexShrink: 0, marginTop: "2px" }}
                  />
                  <Text
                    size={isMobile ? "13px" : "14px"}
                    c={colorScheme === "dark" ? theme.colors.gray[4] : "#393939"}
                    style={{ 
                      lineHeight: isMobile ? "1.3em" : "1.4em",
                      flex: 1,
                    }}
                  >
                    Please complete your profile information to continue. The following fields are required:
                    {missingFields.first_name && " First Name"}
                    {missingFields.last_name && " Last Name"}
                    {missingFields.date_of_birth && " Date of Birth"}
                    {missingFields.address && " Address"}
                  </Text>
                </Group>
              </Box>

              <Stack gap="16px">
                {(missingFields.first_name || missingFields.last_name) && (
                  <Group grow>
                    {missingFields.first_name && (
                      <TextInput
                        label="First Name"
                        name="first_name"
                        placeholder="Enter your first name"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.first_name && formik.errors.first_name}
                        required
                        size="md"
                        styles={{
                          label: {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                            marginBottom: 8,
                            lineHeight: 1.2
                          }
                        }}
                      />
                    )}
                    {missingFields.last_name && (
                      <TextInput
                        label="Last Name"
                        name="last_name"
                        placeholder="Enter your last name"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.last_name && formik.errors.last_name}
                        required
                        size="md"
                        styles={{
                          label: {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                            marginBottom: 8,
                            lineHeight: 1.2
                          }
                        }}
                      />
                    )}
                  </Group>
                )}

                {missingFields.date_of_birth && (
                  <DateInput
                    label="Date of Birth"
                    placeholder="MM/DD/YYYY"
                    value={formik.values.date_of_birth ?? null}
                    name="date_of_birth"
                    id="profile-date-of-birth-input"
                    key="profile-date-of-birth-input"
                    onChange={(date) => {
                      formik.setFieldValue("date_of_birth", date);
                    }}
                    maxDate={new Date()}
                    size="md"
                    required
                    // type="date"
                    autoComplete="bday"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                    popoverProps={{
                      zIndex: 2000,
                      withinPortal: true,
                    }}
                    error={formik.touched.date_of_birth && formik.errors.date_of_birth}
                  />
                )}

                {missingFields.address && (
                  <Stack gap="12px">
                    <Text
                      fw={500}
                      c={colorScheme === "dark" ? "#c1c2c5" : "#495057"}
                      style={{
                        fontSize: "0.875rem",
                        marginBottom: 8,
                        lineHeight: 1.2
                      }}
                    >
                      Address
                    </Text>
                    
                    <TextInput
                      label="Street Address"
                      name="address.street_address"
                      placeholder="Enter your street address"
                      value={formik.values.address.street_address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        (formik.touched.address as any)?.street_address &&
                        (formik.errors.address as any)?.street_address
                      }
                      required
                      size="md"
                      styles={{
                        label: {
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                          marginBottom: 8,
                          lineHeight: 1.2
                        },
                        input: { 
                          height: 40,
                          fontSize: "0.9rem"
                        }
                      }}
                    />
                    
                    <Group grow>
                      <TextInput
                        label="City"
                        name="address.city"
                        placeholder="Enter your city"
                        value={formik.values.address.city}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          (formik.touched.address as any)?.city &&
                          (formik.errors.address as any)?.city
                        }
                        required
                        size="md"
                        styles={{
                          label: {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                            marginBottom: 8,
                            lineHeight: 1.2
                          }
                        }}
                      />
                      <TextInput
                        label="State"
                        name="address.state"
                        placeholder="Enter your state"
                        value={formik.values.address.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          (formik.touched.address as any)?.state &&
                          (formik.errors.address as any)?.state
                        }
                        required
                        size="md"
                        styles={{
                          label: {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                            marginBottom: 8,
                            lineHeight: 1.2
                          }
                        }}
                      />
                    </Group>
                    
                    <SimpleGrid cols={2}>
                      <TextInput
                        label="Zip Code"
                        name="address.zip_code"
                        placeholder="Enter your zip code"
                        value={formik.values.address.zip_code}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                          formik.setFieldValue("address", {
                            ...formik.values.address,
                            zip_code: value,
                          });
                        }}
                        onBlur={formik.handleBlur}
                        maxLength={5}
                        error={
                          (formik.touched.address as any)?.zip_code &&
                          (formik.errors.address as any)?.zip_code
                        }
                        required
                        size="md"
                        styles={{
                          label: {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                            marginBottom: 8,
                            lineHeight: 1.2
                          }
                        }}
                      />
                      <TextInput
                        label="Country"
                        name="address.country"
                        placeholder="Enter your country"
                        value={formik.values.address.country}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          (formik.touched.address as any)?.country &&
                          (formik.errors.address as any)?.country
                        }
                        required
                        size="md"
                        styles={{
                          label: {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                            marginBottom: 8,
                            lineHeight: 1.2
                          }
                        }}
                      />
                    </SimpleGrid>
                  </Stack>
                )}
              </Stack>

              <Button
                type="submit"
                loading={isUpdating}
                disabled={!isFormValid()}
                size="md"
                style={{
                  backgroundColor: isFormValid() ? "#124B8C" : "#9CA3AF",
                  color: "white",
                  fontWeight: 510,
                  fontSize: isMobile ? "13px" : "14px",
                  lineHeight: "1.25em",
                  padding: isMobile ? "8px 20px" : "10px 16px",
                  height: isMobile ? "36px" : "41px",
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                  minWidth: isMobile ? "100px" : "120px",
                  width: isMobile ? "100%" : "auto",
                  cursor: isFormValid() ? "pointer" : "not-allowed",
                }}
              >
                Continue
              </Button>
            </Stack>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default ProfileCompletionModal;

